import {
    Component,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { RouterLink } from "@angular/router";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { BlockComponent } from "@components/container-block/block.component";
import { InputComponent } from "@components/input/input.component";
import { MultiSelectorComponent } from "@components/multi-selector/multi-selector.component";
import { RadiobuttonGroupComponent } from "@components/radiobuttongroup/radiobuttongroup.component";
import { SelectorComponent } from "@components/selector/selector.component";
import { TextareaComponent } from "@components/textarea/textarea.component";
import { Observable, of, timer } from "rxjs";
import { IRadiobuttonItem } from "~app/common/components/radiobuttongroup/radiobutton/IRadiobuttonItem";
import { DisabledControlDirective } from "~app/common/directives/disabled-control/disabled-control.directive";
import { ProduktDTO } from "~app/common/models/produkt/ProduktDTO";
import { ProduktGrunddataResponseDTO } from "~app/common/models/produkt/ProduktGrunddataResponseDTO";
import { FeatureService } from "~app/common/services/feature.service";
import { FormBinderService } from "~app/common/services/form-binder.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { IValidationContext } from "~app/common/validation/IValidationContext";
import { IValidationScope } from "~app/common/validation/IValidationScope";
import { ValidationScopes } from "~app/common/validation/ValidationScopes";
import { ProduktService } from "../../produkt.service";
import { ProduktDetailTexts } from "../produkt-detail.texts";
import {
    FormMode,
    IProduktgruppSelectItem,
    ISearchViol3ProduktModel,
    ISortimentSelectItem,
    ITradslagSelectItem
} from "../produkt-details.models";
import { ProduktDetailHeaderValidation } from "./produkt-detail-header.validation";

@Component({
    selector: "rask-produkt-detail-header",
    templateUrl: "./produkt-detail-header.component.html",
    styleUrls: ["./produkt-detail-header.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        BlockComponent,
        FormsModule,
        ReactiveFormsModule,
        InputComponent,
        SelectorComponent,
        MultiSelectorComponent,
        TextareaComponent,
        CheckboxComponent,
        RadiobuttonGroupComponent,
        DisabledControlDirective,
        RouterLink
    ]
})
export class ProduktDetailHeaderComponent implements OnInit, OnDestroy, OnChanges {
    form: UntypedFormGroup;
    @Input() formMode: FormMode;
    @Input() produkt: ProduktDTO;
    @Input() produktBasdata: ProduktGrunddataResponseDTO;
    produktgrupperItems: IProduktgruppSelectItem[];
    @Input() rootForm: UntypedFormGroup;
    sortimentItems: Observable<ISortimentSelectItem[]>;
    texts = ProduktDetailTexts.productDetailHeader.texts;
    traddelar: IRadiobuttonItem[] = [
        { key: "Rot",
            value: this.texts.traddelar.rot },
        { key: "MellanTopp",
            value: this.texts.traddelar.mellanTopp },
        { key: "AllaStock",
            value: this.texts.traddelar.allaStock }
    ];
    tradslagItems: Observable<ITradslagSelectItem[]>;
    validation: IValidationScope;
    validationContext: IValidationContext;

    private readonly featureService = inject(FeatureService);
    private readonly formBinder = inject(FormBinderService);
    private readonly formBuilder = inject(UntypedFormBuilder);
    private readonly formId = "produkt-detail-header";
    private readonly produktService = inject(ProduktService);

    get produktgruppDisabled(): boolean {
        return this.formMode !== FormMode.New && this.produktgrupperItems.length === 1;
    }

    getInitSelectedViolId = (id: string) => of([
        {
            identitet: id,
            namn: ""
        }
    ]);

    ngOnChanges(changes: SimpleChanges) {
        if (!changes.produkt || !changes.produkt.previousValue || !changes.produkt.previousValue) {
            return;
        }

        this.destroyForm();
        timer(0).subscribe(() => {
            this.createForm();
        });
    }

    ngOnDestroy(): void {
        this.destroyForm();
    }

    ngOnInit(): void {
        this.createForm();
    }

    onProduktgruppChange = ($event): void => {
        this.setHandelssortimentsgrupp(this.produkt.produktgrupp);
    };

    produktgruppFormatter = (item: IProduktgruppSelectItem): number => item ? item.id : undefined;

    produktgruppViewFormatter = (item: IProduktgruppSelectItem): string => item ? item.namn : "";

    searchForViolId = (searchString: string) => this.produktService.searchForViolId(searchString);

    sortimentViewFormatter = (item: ISortimentSelectItem): string => item ? `${item.kod} - ${item.namn}` : "";

    sortimentViewSelectedFormatter = (item: ITradslagSelectItem): string => item ? item.kod : "";

    tradslagViewFormatter = (item: ITradslagSelectItem): string => item ? `${item.kod} - ${item.namn}` : "";

    tradslagViewSelectedFormatter = (item: ITradslagSelectItem): string => item ? item.namn : "";

    violFormatter = (result: ISearchViol3ProduktModel) => result ? `${result.identitet} ${result.namn}` : undefined;

    violOutputFormatter = (result: ISearchViol3ProduktModel) => result ? result.identitet : undefined;

    violSelectedFormatter = (result: ISearchViol3ProduktModel) => result ? result.identitet : undefined;

    private createForm(): void {
        this.produktgrupperItems = this.formMode === FormMode.New
            ? this.produktBasdata.produktgrupper
            : this.produktBasdata.produktgrupper
                .filter(p => p.handelssortimentsgruppId === this.produkt.handelssortimentsgrupp.id);

        this.tradslagItems = of(this.produktBasdata.tradslag);
        this.sortimentItems = of(this.produktBasdata.sortiment);

        this.form = this.formBinder.CreateGroup(this.produkt);

        this.validationContext = ProduktDetailHeaderValidation.init(this.produkt);
        this.validationContext.scope(ValidationScopes.spara).subscribe(v => this.validation = v);
        this.formBinder.attachValidation(this.validationContext.current, this.form);
        this.form.markAsPristine();

        this.rootForm.addControl(this.formId, this.form);
    }

    private destroyForm(): void {
        this.formBinder.detachFromRootForm(this.rootForm, this.formId);
        this.form = undefined;
    }

    private readonly setHandelssortimentsgrupp = (produktgruppid: number): void => {
        if (ObjectUtils.isNullOrUndefined(this.produkt.produktgrupp)) {
            this.produkt.handelssortimentsgrupp = undefined;

            return;
        }

        const produktgrupp = this.produktBasdata.produktgrupper.find(p => p.id === produktgruppid);
        this.produkt.handelssortimentsgrupp = { id: produktgrupp.handelssortimentsgruppId,
            kategori: produktgrupp.kategori };
        this.form.setControl("handelssortimentsgrupp", this.formBuilder.control(this.produkt.handelssortimentsgrupp));
    };
}
